/**
 * SQLite Client — singleton wrapper around sql.js Database.
 *
 * Tanggung jawab:
 * 1. Load sql.js WASM module (via Vite ?url asset import).
 * 2. Load persisted database dari OPFS (primary) atau IndexedDB (fallback).
 * 3. Run schema migrations (PRAGMA user_version).
 * 4. Provide typed query helpers: exec, run, queryAll, queryOne.
 * 5. Debounced persistence — auto-save 2 detik setelah write terakhir.
 *
 * Arsitektur persistensi (docs/01-SRS.md §11):
 *   OPFS (navigator.storage) → primary, ~GB capacity, persisten.
 *   IndexedDB                → fallback jika OPFS tidak tersedia (RSK-02).
 *
 * Catatan: sql.js menyimpan seluruh DB in-memory. Persistensi = export
 * Uint8Array → write ke storage. Debounce mencegah write berlebihan.
 */

import initSqlJs, { type Database as SqlJsDatabase, type SqlValue, type SqlJsStatic } from 'sql.js';
// Vite ?url import — menyalin wasm ke build output, return URL string.
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';

import { runMigrations } from './migrations';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DB_FILENAME = 'ekinkinian.sqlite';
const DEBOUNCE_MS = 2000;

const IDB_NAME = 'ekinkinian-cache';
const IDB_VERSION = 1;
const IDB_STORE = 'sqlite';
const IDB_KEY = 'database';

// ---------------------------------------------------------------------------
// Public Types
// ---------------------------------------------------------------------------

/** Hasil operasi INSERT/UPDATE/DELETE. */
export interface RunResult {
  /** Jumlah baris yang termodifikasi. */
  changes: number;
}

/** Abstraksi database untuk konsumsi oleh Repository Pattern. */
export interface SqliteDatabase {
  /** Eksekusi raw SQL (DDL, multiple statements). Tanpa parameter. */
  exec(sql: string): void;

  /** Eksekusi INSERT/UPDATE/DELETE dengan parameter posisional (?). */
  run(sql: string, params?: SqlValue[]): RunResult;

  /** SELECT — return semua baris sebagai array of objects. */
  queryAll<T>(sql: string, params?: SqlValue[]): T[];

  /** SELECT — return baris pertama atau null. */
  queryOne<T>(sql: string, params?: SqlValue[]): T | null;

  /** Tandai DB perlu di-persist (dipanggil otomatis oleh run). */
  markDirty(): void;

  /** Force persistensi segera (mis. sebelum unload). */
  flush(): Promise<void>;

  /** Tutup database. */
  close(): void;
}

// ---------------------------------------------------------------------------
// Persistence: OPFS (Origin Private File System)
// ---------------------------------------------------------------------------

/** Cek apakah OPFS tersedia di browser ini. */
function isOpfsAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'storage' in navigator &&
    typeof navigator.storage.getDirectory === 'function'
  );
}

/** Simpan Uint8Array database ke OPFS. */
async function opfsSave(data: Uint8Array): Promise<void> {
  const root = await navigator.storage.getDirectory();
  const fileHandle = await root.getFileHandle(DB_FILENAME, { create: true });
  const writable = await fileHandle.createWritable();
  // Cast: sql.js export() selalu menghasilkan ArrayBuffer-backed Uint8Array
  // (bukan SharedArrayBuffer), tetapi TS DOM types mensyaratkan ArrayBuffer secara strict.
  await writable.write(data as unknown as BufferSource);
  await writable.close();
}

/** Baca database dari OPFS. Return null jika belum ada. */
async function opfsLoad(): Promise<Uint8Array | null> {
  const root = await navigator.storage.getDirectory();
  try {
    const fileHandle = await root.getFileHandle(DB_FILENAME);
    const file = await fileHandle.getFile();
    return new Uint8Array(await file.arrayBuffer());
  } catch {
    // File belum ada (first run) — bukan error.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Persistence: IndexedDB (fallback)
// ---------------------------------------------------------------------------

/** Buka (atau buat) database IndexedDB untuk persistensi fallback. */
function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Simpan Uint8Array database ke IndexedDB. */
async function idbSave(data: Uint8Array): Promise<void> {
  const db = await openIndexedDB();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(data, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Baca database dari IndexedDB. Return null jika belum ada. */
async function idbLoad(): Promise<Uint8Array | null> {
  const db = await openIndexedDB();
  try {
    return await new Promise<Uint8Array | null>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const request = tx.objectStore(IDB_STORE).get(IDB_KEY);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

// ---------------------------------------------------------------------------
// Persistence Strategy (OPFS primary, IndexedDB fallback)
// ---------------------------------------------------------------------------

/** Simpan data ke storage (OPFS jika tersedia, selain itu IndexedDB). */
async function persist(data: Uint8Array): Promise<void> {
  if (isOpfsAvailable()) {
    await opfsSave(data);
  } else {
    await idbSave(data);
  }
}

/** Load data dari storage. Return null jika database baru (first run). */
async function loadPersisted(): Promise<Uint8Array | null> {
  if (isOpfsAvailable()) {
    return opfsLoad();
  }
  return idbLoad();
}

// ---------------------------------------------------------------------------
// sql.js Loader
// ---------------------------------------------------------------------------

let sqlStatic: SqlJsStatic | null = null;

/** Load sql.js WASM module (sekali saja, lalu cache). */
async function getSqlJs(): Promise<SqlJsStatic> {
  if (sqlStatic) return sqlStatic;
  sqlStatic = await initSqlJs({ locateFile: () => wasmUrl });
  return sqlStatic;
}

// ---------------------------------------------------------------------------
// Database Wrapper Implementation
// ---------------------------------------------------------------------------

class SqliteDatabaseImpl implements SqliteDatabase {
  private readonly db: SqlJsDatabase;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private dirty = false;

  constructor(db: SqlJsDatabase) {
    this.db = db;
  }

  exec(sql: string): void {
    this.db.exec(sql);
  }

  run(sql: string, params: SqlValue[] = []): RunResult {
    this.db.run(sql, params);
    const changes = this.db.getRowsModified();
    this.markDirty();
    return { changes };
  }

  queryAll<T>(sql: string, params: SqlValue[] = []): T[] {
    const stmt = this.db.prepare(sql);
    if (!stmt) return [];
    try {
      stmt.bind(params);
      const rows: T[] = [];
      while (stmt.step()) {
        rows.push(stmt.getAsObject() as unknown as T);
      }
      return rows;
    } finally {
      stmt.free();
    }
  }

  queryOne<T>(sql: string, params: SqlValue[] = []): T | null {
    return this.queryAll<T>(sql, params)[0] ?? null;
  }

  markDirty(): void {
    this.dirty = true;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      void this.flush();
    }, DEBOUNCE_MS);
  }

  async flush(): Promise<void> {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    if (!this.dirty) return;
    this.dirty = false;
    const data = this.db.export();
    await persist(data);
  }

  close(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.db.close();
  }
}

// ---------------------------------------------------------------------------
// Singleton Initialization
// ---------------------------------------------------------------------------

let dbInstance: SqliteDatabase | null = null;
let initPromise: Promise<SqliteDatabase> | null = null;

/**
 * Inisialisasi database SQLite cache.
 *
 * Langkah:
 * 1. Load sql.js WASM.
 * 2. Load database dari storage (OPFS/IndexedDB). Jika belum ada, buat baru.
 * 3. Run migrations (create tables if not exist).
 * 4. Setup auto-flush pada visibilitychange.
 *
 * Idempoten — aman dipanggil berkali-kali, return instance yang sama.
 */
export function initDatabase(): Promise<SqliteDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (initPromise) return initPromise;

  initPromise = doInit()
    .then((db) => {
      dbInstance = db;
      initPromise = null;
      return db;
    })
    .catch((error) => {
      initPromise = null;
      throw error;
    });

  return initPromise;
}

/** Internal: actual initialization logic. */
async function doInit(): Promise<SqliteDatabase> {
  // 1. Load WASM
  const SQL = await getSqlJs();

  // 2. Load persisted database (atau buat baru)
  const persistedData = await loadPersisted();
  const rawDb = persistedData ? new SQL.Database(persistedData) : new SQL.Database();

  // 3. Run migrations
  const result = runMigrations(rawDb);
  if (result.applied > 0) {
    console.info(
      `[db] Migrations applied: v${result.fromVersion} → v${result.toVersion} (${result.applied} migration(s))`,
    );
  } else {
    console.info(`[db] Schema up to date (v${result.toVersion})`);
  }

  // 4. Wrap + setup auto-flush
  const db = new SqliteDatabaseImpl(rawDb);
  setupAutoFlush(db);

  // 5. Persist immediately if migrations created tables (new DB)
  if (result.applied > 0) {
    await db.flush();
  }

  const storageType = isOpfsAvailable() ? 'OPFS' : 'IndexedDB';
  console.info(`[db] SQLite cache ready (storage: ${storageType})`);

  return db;
}

/** Auto-flush saat user beralih tab (visibilitychange → hidden). */
function setupAutoFlush(db: SqliteDatabase): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void db.flush();
    }
  });
}

/**
 * Ambil instance database yang sudah terinisialisasi (synchronous).
 * Return null jika initDatabase() belum dipanggil.
 */
export function getDatabase(): SqliteDatabase | null {
  return dbInstance;
}

/**
 * Ambil instance database, throw jika belum diinisialisasi.
 * Gunakan di Repository methods yang membutuhkan akses sinkron.
 */
export function requireDatabase(): SqliteDatabase {
  const db = dbInstance;
  if (!db) {
    throw new Error(
      'Database belum diinisialisasi. Panggil initDatabase() terlebih dahulu (biasanya di +layout.svelte onMount).',
    );
  }
  return db;
}
